import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { AppIcon } from './ui/icon'
import { mdiCheck, mdiClose, mdiCheckAll  } from '@mdi/js'
import { useAssetProperties, isPendingApprovalValue, usePropertyCategories, refreshAssetProperties } from '../hooks/useQueries'
import { apiClient } from '../api/client'
import { WizardStepHeader } from './WizardStepHeader'
import { SECTION_ICONS } from '../lib/section-icons'

interface PropertyQuestionnaireStepProps {
  org: string
  assetUid: string
  /** Advance to the wizard's next step (Fiabilisation, spec 2026-06-29 §3). No longer the wizard's final step. */
  onNext: () => void
  onFillLater: () => void
  onBack: () => void
}

interface CompletenessData {
  score: number
  mandatory: { total: number; filled: number }
  recommended: { total: number; filled: number }
}

export function PropertyQuestionnaireStep({
  org,
  assetUid,
  onNext,
  onFillLater,
  onBack,
}: PropertyQuestionnaireStepProps) {
  const { t, i18n } = useTranslation('assets')
  const { t: tCommon } = useTranslation('common')
  const { t: tProps } = useTranslation('properties')
  const queryClient = useQueryClient()
  const locale = i18n.language || 'en'

  // Data — single query returns confirmed + pending-approval values; split client-side
  const { data: allProperties = [] } = useAssetProperties(org, assetUid)
  const properties = useMemo(() => allProperties.filter(p => !isPendingApprovalValue(p)), [allProperties])
  const pendingValues = useMemo(() => allProperties.filter(p => isPendingApprovalValue(p)), [allProperties])
  const { data: categories = [] } = usePropertyCategories(org)

  const [completeness, setCompleteness] = useState<CompletenessData | null>(null)
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [manualValues, setManualValues] = useState<Record<string, string>>({})
  const [savingProperty, setSavingProperty] = useState<string | null>(null)
  const [processing, setProcessing] = useState<Set<string>>(new Set())

  // Fetch completeness
  const fetchCompleteness = useCallback(async () => {
    try {
      const { data } = await apiClient.GET('/api/v1/organizations/{orgCode}/assets/{assetUid}/completeness' as any, {
        params: { path: { orgCode: org, assetUid } }
      })
      if (data) setCompleteness(data as any)
    } catch {
      // Completeness is optional
    }
  }, [org, assetUid])

  useEffect(() => { fetchCompleteness() }, [fetchCompleteness])

  // Group properties by category
  const grouped = useMemo(() => {
    const allProps = [...properties, ...pendingValues].filter(p => {
      const pd = (p as any).property_definition
      return !pd?.flags?.includes('hidden')
    })
    const map = new Map<string, { name: string; properties: any[] }>()

    for (const p of allProps) {
      const pd = (p as any).property_definition
      const catId = pd?.property_category_id || 'other'
      const cat = categories.find((c: any) => c.id === catId || c.uid === catId)
      const catName = cat ? (cat.name?.[locale] || cat.name?.en || cat.code || 'Other') : t('create_building.questionnaire.other_section')

      if (!map.has(catId)) {
        map.set(catId, { name: catName, properties: [] })
      }
      map.get(catId)!.properties.push(p)
    }

    return Array.from(map.entries())
  }, [properties, pendingValues, categories, locale, t])

  // Auto-select first section
  useEffect(() => {
    if (!selectedSection && grouped.length > 0) {
      setSelectedSection(grouped[0][0])
    }
  }, [grouped, selectedSection])

  const approveValue = useCallback(async (uid: string) => {
    setProcessing(prev => new Set(prev).add(uid))
    try {
      await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve', {
        params: { path: { orgCode: org, assetUid } },
        body: { approve: [uid], reject: [] },
      })
      refreshAssetProperties(queryClient, org, assetUid)
      fetchCompleteness()
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(uid); return s })
    }
  }, [org, assetUid, queryClient, fetchCompleteness])

  const rejectValue = useCallback(async (uid: string) => {
    setProcessing(prev => new Set(prev).add(uid))
    try {
      await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve', {
        params: { path: { orgCode: org, assetUid } },
        body: { approve: [], reject: [uid] },
      })
      refreshAssetProperties(queryClient, org, assetUid)
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(uid); return s })
    }
  }, [org, assetUid, queryClient])

  const approveAllHighConfidence = useCallback(async () => {
    await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve', {
      params: { path: { orgCode: org, assetUid } },
      body: { approve: [], reject: [], min_confidence: 0.85 },
    })
    refreshAssetProperties(queryClient, org, assetUid)
    fetchCompleteness()
  }, [org, assetUid, queryClient, fetchCompleteness])

  const saveManualValue = useCallback(async (propertyUid: string) => {
    const value = manualValues[propertyUid]
    if (!value?.trim()) return
    setSavingProperty(propertyUid)
    try {
      await apiClient.PUT('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
        params: { path: { orgCode: org, assetUid, propertyUid } },
        body: { value } as any,
      })
      refreshAssetProperties(queryClient, org, assetUid)
      fetchCompleteness()
      setManualValues(prev => { const n = { ...prev }; delete n[propertyUid]; return n })
    } finally {
      setSavingProperty(null)
    }
  }, [org, assetUid, queryClient, manualValues, fetchCompleteness])

  const getPropertyName = (p: any) => {
    const pd = p.property_definition
    if (!pd?.name) return pd?.code || p.property_uid
    return pd.name[locale] || pd.name.en || pd.code || ''
  }

  const isPending = (p: any) => isPendingApprovalValue(p)

  const totalProps = completeness ? completeness.mandatory.total + completeness.recommended.total : 0
  const filledProps = completeness ? completeness.mandatory.filled + completeness.recommended.filled : 0
  const highConfidenceCount = pendingValues.filter((p: any) => (p.source_details?.confidence ?? 0) >= 0.85).length

  const currentSection = selectedSection ? grouped.find(([id]) => id === selectedSection) : grouped[0]

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-card rounded-lg p-6">
        <WizardStepHeader step={4} title={t('create_building.questionnaire.title')} />

        {/* Completeness bar */}
        {completeness && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                {t('create_building.questionnaire.completeness', { score: completeness.score })}
              </span>
              <span className="text-sm text-muted-foreground">
                {filledProps}/{totalProps} {t('create_building.questionnaire.properties')}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5">
              <div className="bg-primary h-2.5 rounded-full transition-all" style={{ width: `${completeness.score}%` }} />
            </div>
          </div>
        )}

        {/* Batch approve */}
        {highConfidenceCount > 0 && (
          <div className="mb-4">
            <Button variant="outline" size="sm" onClick={approveAllHighConfidence} data-testid="batch-approve-all-btn">
              <AppIcon path={mdiCheckAll} size="sm" className="mr-1" />
              {tProps('pending_approvals.confirm_high_confidence', { count: highConfidenceCount })}
            </Button>
          </div>
        )}

        {/* Two-column layout */}
        <div className="grid grid-cols-[200px_1fr] gap-6">
          {/* Sidebar */}
          <div className="space-y-1">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('create_building.questionnaire.sections')}</h4>
            {grouped.map(([id, group]) => {
              const confirmed = group.properties.filter((p: any) => !isPending(p) && p.value != null).length
              const total = group.properties.length
              return (
                <button
                  key={id}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedSection === id ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'
                  }`}
                  onClick={() => setSelectedSection(id)}
                  data-testid={`section-${id}`}
                >
                  <span className="flex items-center justify-between">
                    <span className="truncate">{group.name}</span>
                    <span className="text-xs">{confirmed}/{total}</span>
                  </span>
                </button>
              )
            })}
          </div>

          {/* Content */}
          <div className="space-y-4">
            {currentSection && (
              <>
                <h3 className="text-lg font-medium">{currentSection[1].name}</h3>

                {/* Pending approvals first */}
                {currentSection[1].properties.filter(isPending).map((p: any) => (
                  <div key={p.uid || p.property_uid} className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50/50 dark:bg-amber-900/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{getPropertyName(p)}</span>
                      {p.source_details?.confidence != null && (
                        <span className="text-xs text-muted-foreground">
                          {Math.round(p.source_details.confidence * 100)}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono flex-1">
                        {typeof p.value === 'string' ? p.value : JSON.stringify(p.value)}
                      </span>
                      {p.source_details?.document_uid && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <AppIcon path={SECTION_ICONS.documents} size="xs" />
                          {p.source_details.document_uid.slice(0, 8)}
                        </span>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 text-green-600" onClick={() => approveValue(p.uid)} disabled={processing.has(p.uid)} data-testid={`approve-value-${p.uid}`}>
                        <AppIcon path={mdiCheck} size="xs" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-destructive" onClick={() => rejectValue(p.uid)} disabled={processing.has(p.uid)} data-testid={`reject-value-${p.uid}`}>
                        <AppIcon path={mdiClose} size="xs" />
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Confirmed values */}
                {currentSection[1].properties.filter((p: any) => !isPending(p) && p.value != null).map((p: any) => (
                  <div key={p.uid || p.property_uid} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{getPropertyName(p)}</span>
                      <AppIcon path={mdiCheck} size="xs" className="text-green-600" />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {typeof p.value === 'string' ? p.value : JSON.stringify(p.value)}
                    </span>
                  </div>
                ))}

                {/* Empty fields for manual fill */}
                {currentSection[1].properties.filter((p: any) => !isPending(p) && p.value == null).length > 0 && (
                  <>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mt-4">
                      {t('create_building.questionnaire.to_fill_manually')}
                    </div>
                    {currentSection[1].properties.filter((p: any) => !isPending(p) && p.value == null).map((p: any) => (
                      <div key={p.uid || p.property_uid} className="border border-dashed border-border rounded-lg p-4">
                        <span className="text-sm font-medium block mb-2">{getPropertyName(p)}</span>
                        <div className="flex gap-2">
                          <Input
                            value={manualValues[p.property_uid] || ''}
                            onChange={(e) => setManualValues(prev => ({ ...prev, [p.property_uid]: e.target.value }))}
                            placeholder={getPropertyName(p)}
                            className="flex-1"
                            data-testid={`manual-input-${p.property_uid}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => saveManualValue(p.property_uid)}
                            disabled={savingProperty === p.property_uid || !manualValues[p.property_uid]?.trim()}
                            data-testid={`save-manual-${p.property_uid}`}
                          >
                            {tCommon('actions.save')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 pt-6 border-t border-border flex justify-between">
          <Button variant="outline" onClick={onBack} data-testid="questionnaire-back-btn">
            {tCommon('actions.back')}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onFillLater} data-testid="questionnaire-fill-later-btn">
              {t('create_building.questionnaire.fill_later')}
            </Button>
            <Button onClick={onNext} className="bg-primary hover:bg-primary/90" data-testid="questionnaire-next-btn">
              {tCommon('actions.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
